import { useEffect, useMemo, useSyncExternalStore } from 'react';

import type { InstallationInfo } from '@sb/firmware-info/types';
import { makeNamespacedLog } from '@sb/log';
import type { GetFirmwareInfoResult } from '@sb/messenger/auto_generated/messenger/arm_control_bot_service';
import { JointConnectionStatus } from '@sb/messenger/auto_generated/messenger/arm_control_bot_service';
import { getService } from '@sbrc/services/feathers-client/utils';

const log = makeNamespacedLog('useHardwareConfigFirmwareData');

const POLL_INTERVAL_MS = 5000; // Poll every 5 seconds
const MAX_CONSECUTIVE_ERRORS = 3;

export interface HardwareConfigFirmwareResult {
  /** Firmware info from GetFirmwareInfo, ready for use in SoftwareBuildData */
  activeFirmwareInfo: Array<InstallationInfo & { jointNumber: number }>;
  /** Map of joint number to connection status from FirmwareInfo */
  jointConnectionStatus: Map<number, 'disconnected' | 'connected' | 'ready'>;
  /** Set of joint numbers marked as fake (JointFakeConfig in arm-control-bot.json) */
  fakeJoints: Set<number>;
}

// ---------------------------------------------------------------------------
// Module-level shared polling store
//
// All hook instances share a single polling interval. When at least one
// consumer subscribes with `enabled=true`, polling starts. When the last
// enabled consumer unmounts, polling stops. Consumers with `enabled=false`
// still receive data from the shared store (including a one-time initial
// fetch if no poller is active yet).
// ---------------------------------------------------------------------------

let sharedFirmwareInfo: GetFirmwareInfoResult | null = null;
const listeners = new Set<() => void>();
let pollingInterval: NodeJS.Timeout | null = null;
let enabledSubscriberCount = 0;
let consecutiveErrors = 0;
let initialFetchDone = false;

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

async function fetchFirmwareInfo() {
  try {
    const service = getService('firmwareInfo')();

    const result = (await service.find({})) as unknown as GetFirmwareInfoResult;

    consecutiveErrors = 0;
    sharedFirmwareInfo = result;
    initialFetchDone = true;
    notify();
  } catch (error) {
    consecutiveErrors += 1;

    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
      // Clear stale data so the UI reflects that ACB is unreachable
      // rather than showing outdated connection statuses.
      sharedFirmwareInfo = null;
      notify();
    }

    log.warn(
      'fetch.error',
      `Error fetching FirmwareInfo (consecutive: ${consecutiveErrors})`,
      error,
    );
  }
}

function startPolling() {
  enabledSubscriberCount += 1;

  if (enabledSubscriberCount === 1) {
    // First enabled subscriber — kick off polling
    fetchFirmwareInfo();
    pollingInterval = setInterval(fetchFirmwareInfo, POLL_INTERVAL_MS);
  }
}

function stopPolling() {
  enabledSubscriberCount -= 1;

  if (enabledSubscriberCount === 0 && pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): GetFirmwareInfoResult | null {
  return sharedFirmwareInfo;
}

/**
 * Hook to fetch real-time firmware info from arm-control-bot.
 * Disconnected joints (absent from GetFirmwareInfo response) are not included
 * in activeFirmwareInfo and should be treated as needing an update.
 *
 * All consumers share a single polling interval. When at least one consumer
 * passes `enabled=true`, the shared poller runs every 5 seconds. Consumers
 * with `enabled=false` receive the shared data and trigger a one-time fetch
 * if no data has been fetched yet.
 *
 * @param enabled - Whether this consumer needs active polling (default: false).
 * @param options.skip - When true, suppresses all fetching. Use when the
 *   underlying service is not available.
 */
export function useHardwareConfigFirmwareData(
  enabled: boolean = false,
  { skip = false }: { skip?: boolean } = {},
): HardwareConfigFirmwareResult {
  const firmwareInfo = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    if (skip) return undefined;

    if (enabled) {
      startPolling();

      return () => {
        stopPolling();
      };
    }

    // When disabled, do a one-time fetch if no data has been fetched yet
    if (!initialFetchDone) {
      fetchFirmwareInfo();
    }

    return undefined;
  }, [enabled, skip]);

  return useMemo((): HardwareConfigFirmwareResult => {
    const activeFirmwareInfo: Array<
      InstallationInfo & { jointNumber: number }
    > = [];

    const jointConnectionStatus = new Map<
      number,
      'disconnected' | 'connected' | 'ready'
    >();

    const fakeJoints = new Set<number>();

    if (firmwareInfo?.joints) {
      for (const jointInfo of firmwareInfo.joints) {
        const { jointNumber } = jointInfo;

        if (jointNumber === undefined) {
          continue;
        }

        // Track fake joints (marked as JointFakeConfig in arm-control-bot.json)
        // Skip them entirely - they have no real firmware to track
        if (jointInfo.isFake) {
          fakeJoints.add(jointNumber);
          continue;
        }

        // Map connection status
        let connectionStatus: 'disconnected' | 'connected' | 'ready' =
          'disconnected';

        switch (jointInfo.connectionStatus) {
          case JointConnectionStatus.JOINT_DISCONNECTED:
            connectionStatus = 'disconnected';
            break;
          case JointConnectionStatus.JOINT_CONNECTED:
            connectionStatus = 'connected';
            break;
          case JointConnectionStatus.JOINT_READY:
            connectionStatus = 'ready';
            break;
          default:
            connectionStatus = 'disconnected';
        }

        jointConnectionStatus.set(jointNumber, connectionStatus);

        // Installed CM7/CM4 checksums ACB queried over CAN (int32; size not
        // reported, so left unset).
        const hasInstalledChecksums =
          jointInfo.installedCm7Checksum !== undefined ||
          jointInfo.installedCm4Checksum !== undefined;

        const hasTargetChecksums =
          jointInfo.targetCm7Checksum !== undefined ||
          jointInfo.targetCm4Checksum !== undefined;

        activeFirmwareInfo.push({
          status: 'installed',
          updatedAt: new Date(),
          jointNumber,
          buildInfo: {
            buildId: jointInfo.firmwareBuildId,
            variant: jointInfo.firmwareVariant,
          },
          firmwareUpToDate: jointInfo.firmwareUpToDate,
          ...(hasInstalledChecksums
            ? {
                binaryInfo: {
                  cm7checksum: jointInfo.installedCm7Checksum,
                  cm4checksum: jointInfo.installedCm4Checksum,
                },
              }
            : {}),
          // Target checksums — for the inspector's "Target Build Binary" row.
          ...(hasTargetChecksums
            ? {
                targetBinaryInfo: {
                  cm7checksum: jointInfo.targetCm7Checksum,
                  cm4checksum: jointInfo.targetCm4Checksum,
                },
              }
            : {}),
        });
      }
    }

    return { activeFirmwareInfo, jointConnectionStatus, fakeJoints };
  }, [firmwareInfo]);
}
